import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import ImageUploadComponent from 'components/ImageUploadComponent';
import { Loading } from 'components/loading/Loading';
import ViewImageModal from 'components/ViewItemImageModal';
import React, { useEffect, useState } from 'react';
import { fetchCategoryDetails, toggleCategoryActiveStatus, updateCategory, updateCategoryImage } from 'services';
import { showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import _ from 'lodash';

const CategoryDetails = props => {
  const [loading, setLoading] = useState<boolean>(true);
  const [category, setCategory] = useState<Record<string, any>>({});
  const [updatedCategory, setUpdatedCategory] = useState<Record<string, any>>({});
  const [viewImageModalOpen, setViewImageModalOpen] = useState<boolean>(false);
  const [editBasicInfo, setEditBasicInfo] = useState<boolean>(false);
  const [editSEOFields, setEditSEOFields] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [seoUpdateLoading, setSEOUpdateLoading] = useState<boolean>(false);
  const [activeLoading, setActiveLoading] = useState<boolean>(false);
  const [isFreshCutLoading, setIsFreshCutLoading] = useState<boolean>(false);
  const { categoryId } = props.match.params;
  
  const fetchCategoryDetailsFn = async () => {
    try {
      const response = await fetchCategoryDetails(categoryId);
      setCategory(response);
      setUpdatedCategory(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryDetailsFn();
  }, []);
  
  const handleUpdate = async (payload: Record<string, any>) => {
    const data = {
      ...payload,
      id: categoryId,
    };
    try {
      await updateCategory(data);
      showSuccess('Category updated successfully');
    } catch (e) {
      showError(e);
    } finally {
      
    }
  };
  
  const toggleActive = async () => {
    setActiveLoading(true);
    try {
      await updateCategory({
        id: categoryId,
        active: !category.active,
      });
      category.active = !category.active;
      setCategory(category);
      showSuccess(`Category marked as ${category.active ? 'active' : 'inactive'} successfully.`);
    } catch (e) {
      showError(e);
    } finally {
      setActiveLoading(false);
    }
  };

  const updateIsFreshCut = async (e) => {
    setIsFreshCutLoading(true);
    try {
      const newVal = e.target.checked;
      category.isFreshCut = newVal;
      await updateCategory({
        id: categoryId,
        isFreshCut: newVal,
      });
      setCategory(category);
      showSuccess(`Update Fresh Cut flag successfully.`);
    } catch (e) {
      showError(e);
    } finally {
      setIsFreshCutLoading(false);
    }
  };

  const handleEditCancel = (fields: string[], isSeo: boolean) => {
    setUpdatedCategory({
      ...updatedCategory,
      ..._.pick(category, fields),
    });
    if (isSeo) {
      setEditSEOFields(false);
    } else {
      setEditBasicInfo(false);
    }
  };
  
  const handleBasicDetailsUpdate = async () => {
    setUpdateLoading(true);
    await handleUpdate({
      taxPercent: updatedCategory.taxPercent,
      sort: Number(updatedCategory.sort),
      storageClass: updatedCategory.storageClass,
    });
    setEditBasicInfo(!editBasicInfo);
    setUpdateLoading(false);
  };
  
  const handleSEOPrimaryAction = async () => {
    if (editSEOFields) {
      setSEOUpdateLoading(true);
      await handleUpdate({
        description: updatedCategory.description,
        enDescription: updatedCategory.enDescription,
        metaDescription: updatedCategory.metaDescription,
        enMetaDescription: updatedCategory.enMetaDescription,
        metaTitle: updatedCategory.metaTitle,
        enMetaTitle: updatedCategory.enMetaTitle,
      });
      setSEOUpdateLoading(false);
    }
    setEditSEOFields(!editSEOFields);
  };
  
  const handleEdit = (key: string, value: string | number) => {
    setUpdatedCategory({
      ...updatedCategory,
      [key]: value,
    });
  };

  const EditableDetailsGroup = (
    { label,
      detail = '',
      isSeo = false,
      isTextArea = false,
      inputType= 'text',
      propertyKey,
    }:
    {
      label: string;
      detail: string;
      isSeo?: boolean;
      isTextArea?: boolean;
      inputType?: string;
      propertyKey: string;
    },
  ) => {
    const editable = isSeo ? editSEOFields : editBasicInfo;
    const loading = isSeo ? seoUpdateLoading : updateLoading;
    
    return (
      <div className={styles['info-group']}>
        <div className={styles['label']}>{label}</div>
        {editable ? (
          isTextArea ? (
            <textarea
              key={propertyKey}
              disabled={loading}
              className={'form-control'}
              value={detail}
              onChange={e => handleEdit(propertyKey, e.target.value)}
            />
          ) : (
            <input
              key={propertyKey}
              type={inputType}
              disabled={loading}
              className={'form-control'}
              value={detail}
              onChange={e => handleEdit(propertyKey, e.target.value)}
            />
          )
        ) : (
          <div className={styles['info']}>{detail || '-'}</div>
        )}
      </div>
    );
  };

  const getSEOFields = () => (
    <div className={styles['seo-section']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>SEO Fields</div>
        <div className={'d-flex'}>
          <ActionButton
            text={editSEOFields ? 'Save' : 'Edit'}
            active={true}
            isLoading={false}
            onClick={handleSEOPrimaryAction}
          />
          {editSEOFields && (
            <ActionButton
              text={'Cancel'}
              active={true}
              isLoading={false}
              customClass={styles['cancel-button']}
              onClick={() => handleEditCancel([
                'description',
                'enDescription',
                'metaDescription',
                'enMetaDescription',
                'metaTitle',
                'enMetaTitle',
                'metaDescription',
                'enMetaDescription',
              ], true)}
            />
          )}
        </div>
      </div>
      <div className={styles['fields-section']}>
        {EditableDetailsGroup({
          detail: updatedCategory.description,
          label: 'Spanish Name',
          isSeo: true,
          propertyKey: 'description',
        })}
        {EditableDetailsGroup({
          detail: updatedCategory.enDescription,
          label: 'English Name',
          isSeo: true,
          propertyKey: 'enDescription',
        })}
        {/* {EditableDetailsGroup({
          detail: updatedCategory.metaDescription,
          label: 'Spanish Description',
          isSeo: true,
          propertyKey: 'metaDescription',
          isTextArea: true,
        })}
        {EditableDetailsGroup({
          detail: updatedCategory.enMetaDescription,
          label: 'English Description',
          isSeo: true,
          propertyKey: 'enMetaDescription',
          isTextArea: true,
        })} */}
        {EditableDetailsGroup({
          detail: updatedCategory.metaTitle,
          label: 'Spanish Meta Title',
          isSeo: true,
          propertyKey: 'metaTitle',
        })}
        {EditableDetailsGroup({
          detail: updatedCategory.enMetaTitle,
          label: 'English Meta Title',
          isSeo: true,
          propertyKey: 'enMetaTitle',
        })}
        {EditableDetailsGroup({
          detail: updatedCategory.metaDescription,
          label: 'Spanish Meta Description',
          isSeo: true,
          propertyKey: 'metaDescription',
          isTextArea: true,
        })}
        {EditableDetailsGroup({
          detail: updatedCategory.enMetaDescription,
          label: 'English Meta Description',
          isSeo: true,
          propertyKey: 'enMetaDescription',
          isTextArea: true,
        })}
      </div>
    </div>
  );

  const getEditableBasicDetailsSection = () => (
    <div className={styles['editable-basic-details-section']}>
      <div className={styles['info-group']}>
        <div className={styles['label']}>Tax Percent</div>
        {editBasicInfo ? (
          <input
            className={'form-control'}
            type='number'
            min={0.0}
            step={0.1}
            disabled={updateLoading}
            value={updatedCategory.taxPercent}
            onChange={e => handleEdit('taxPercent', parseFloat(e.target.value || '0'))}
          />
        ) : (
          <div className={styles['info']}>{updatedCategory.taxPercent}</div>
        )}
      </div>
      {EditableDetailsGroup({
        detail: updatedCategory.storageClass,
        label: 'Storage Class',
        propertyKey: 'storageClass',

      })}
      {EditableDetailsGroup({
        detail: updatedCategory.sort,
        label: 'Sort',
        inputType: 'number',
        propertyKey: 'sort',
      })}
    </div>
  );
  
  const getBasicDetailsSection = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['image-container']}>
        <ImageUploadComponent
          src={category.image?.url}
          entityId={category.objectId}
          toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
          uploadAPI={updateCategoryImage}
        />
      </div>
      <div className={styles['info-container']}>
        <div className={styles['info-section']}>
          <div className={`${styles['info-group']}`}>
            <div className={styles['label']}>Name</div>
            <div className={styles['info']}>{category.description}</div>
          </div>
          <div className={`${styles['info-group']}`}>
            <div className={styles['label']}>English Name</div>
            <div className={styles['info']}>{category.enDescription}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Category ID</div>
            <div className={styles['info']}>{category.objectId}</div>
          </div>
          <div className={styles['category-active']}>
            <div className={styles['active-input-container']}>
              {activeLoading ? (
                <Loading type={'mini'} />
              ) : (
                <input
                  type={'checkbox'}
                  disabled={activeLoading}
                  checked={category.active}
                  onChange={toggleActive}
                />
              )}
            </div>
            <div className={styles['info']}>Active</div>
          </div>
          <div className={styles['category-active']}>
            <div className={styles['active-input-container']}>
              {isFreshCutLoading ? (
                <Loading type={'mini'} />
              ) : (
                <input
                  type={'checkbox'}
                  disabled={isFreshCutLoading}
                  checked={category.isFreshCut}
                  onChange={updateIsFreshCut}
                />
              )}
            </div>
            <div className={styles['info']}>Is Fresh Cut</div>
          </div>
        </div>
        <div className={styles['action-section']}>
          {editBasicInfo ? (
            <div className={'d-flex'}>
              <ActionButton
                active={!updateLoading}
                isLoading={updateLoading}
                onClick={handleBasicDetailsUpdate}
                text={'Save'}
              />
              <ActionButton
                active={!updateLoading}
                isLoading={false}
                onClick={() => handleEditCancel(['taxPercent', 'sort', 'storageClass'], false)}
                text={'Cancel'}
                customClass={styles['cancel-button']}
              />
            </div>
          ) : (
            <div onClick={() => setEditBasicInfo(!editBasicInfo)} className={styles['edit']}>Edit</div>
          )}
        </div>
      </div>
    </div>
  );

  const getHeaderSection = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Category Details</div>
      <BackButton />
    </div>
  );
  
  if (loading) return <Loading />;
  
  return (
    <>
      {
        viewImageModalOpen && (
          <ViewImageModal
            originalAspect={true}
            source={category.image?.url}
            toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
          />
        )
      }
      <div className={styles['main-container']}>
        {getHeaderSection()}
        {getBasicDetailsSection()}
        {getEditableBasicDetailsSection()}
        {getSEOFields()}
      </div>
    </>
  );
};

export { CategoryDetails };